import {
  usePerformaceEvaluationCategory,
  useUserFormFieldsQuery,
} from "@/hooks/services";
import type { DeimensionItem, UserInfoResult } from "@/types/apis";
import { Form } from "antd";
import { useCallback, useEffect, useState } from "react";
import type {
  EvaluationDataType,
  FnUpdateInvitePerson,
  InvitePerformanceEvaluationDetailItem,
} from "../type";
import { usePerformanceEvaluationAdviceBehavior } from "./usePerformanceEvaluationAdviceBehavior";
import { usePerformanceEvaluationResultQuery } from "./usePerformanceEvaluationResultQuery";
import { useSupplierBasicFormInit } from "./useSupplierBasicFormInit";
import { useModal } from "@/hooks/modals/useModal";

/**
 * 根据部门过滤考核维度
 */
function filterDimensionsByDept(
  dimensions: DeimensionItem[],
  deptNo: string,
  isSelf: boolean,
): DeimensionItem[] {
  return dimensions
    ?.map((d) => ({
      ...d,
      weights: d.weights.filter((w) =>
        isSelf
          ? w.ratingDeptId.toString() === deptNo
          : w.ratingDeptId.toString() !== deptNo,
      ),
    }))
    .filter((d) => d.weights.length > 0);
}

/**
 * 构建我的评分数据
 */
function buildSelfEvaluationData(
  dimensions: DeimensionItem[],
  userInfo: UserInfoResult,
): EvaluationDataType[] {
  const data: EvaluationDataType[] = [];

  dimensions.forEach((dim) => {
    dim.weights.forEach((item) => {
      data.push({
        key: item.indicatorId,
        dimensionId: dim.dimensionId,
        dimensionName: dim.dimensionName,
        indicatorId: item.indicatorId,
        indicator2Name: item.indicator2Name,
        level1Remarks: item.level1Remarks,
        level2Remarks: item.level2Remarks,
        level3Remarks: item.level3Remarks,
        level4Remarks: item.level4Remarks,
        score: 0,
        weight: item.weight,
        ratingDeptId: userInfo.deptNo,
        rater: userInfo.userId,
      });
    });
  });

  return data;
}

/**
 * 构建邀请评估数据
 */
function buildInviteEvaluationData(
  dimensions: DeimensionItem[],
): InvitePerformanceEvaluationDetailItem[] {
  const data: InvitePerformanceEvaluationDetailItem[] = [];

  dimensions.forEach((dim) => {
    dim.weights.forEach((item) => {
      data.push({
        weight: item.weight,
        indicatorId: item.indicatorId,
        ratingDeptId: item.ratingDeptId,
        ratingDeptName: item.ratingDeptName,
        score: null,
        scoreLevel: null,
        rater: null,
      });
    });
  });

  return data;
}

/**
 * 供应商绩效考核 Modal Hook
 * 负责管理表单状态、评分数据、初始化逻辑
 */
export function useSupplierPerformanceAppraisalModal(params: {
  editingId?: number | null;
  appraisalModalOpen: boolean;
}) {
  const { editingId, appraisalModalOpen } = params;

  const { props } = useModal("PERFORMANCE_EVALUATION_NOTIFICATION");
  const { mode, viewStatus } = props;
  const isInReview = mode === "inReview";
  const isStarted = mode === "started";

  const [form] = Form.useForm();

  // 查询绩效考核结果数据
  const { queryResult, evalutionResultData } =
    usePerformanceEvaluationResultQuery(editingId);

  // 建议表单相关
  const { form: adviceForm, submitAdvice } =
    usePerformanceEvaluationAdviceBehavior();

  // 初始化表单数据
  useSupplierBasicFormInit({
    queryResult,
    form,
    adviceForm,
    open: appraisalModalOpen,
  });

  const { categories } = usePerformaceEvaluationCategory();

  // 监控供应商分类ID
  const supplierCategoryId = Form.useWatch("supplierCategoryId", form);

  // 获取用户信息
  const userInfo = useUserFormFieldsQuery();
  console.log({ queryResult }, { userInfo });

  // 根据用户部门过滤维度
  const { selfDimensions, inviteDimensions } = useEvaluationDimensions({
    categories,
    supplierCategoryId,
    userInfo,
  });

  // 我的评分数据
  const { evaluationData, onScoreChange, setEvaluationData } =
    useSelfEvaluationData({
      dimensions: selfDimensions,
      userInfo,
    });

  // 邀请评估数据
  const {
    inviteEvaluationDetails,
    onInvitePersonChange,
    setInviteEvaluationDetails,
  } = useInviteEvaluationData({
    dimensions: inviteDimensions,
  });

  const adviceProcessDoneRules = [
    queryResult?.evaluationAdvice && queryResult.evaluationStatus === 2,
    isInReview && viewStatus !== true,
    isStarted && queryResult && queryResult?.createBy !== userInfo?.userId,
  ];
  // 判断建议流程是否已完成
  const adviceProcessDone = adviceProcessDoneRules.some((rule) => rule);

  // 弹窗关闭时重置所有状态
  useEffect(() => {
    if (!appraisalModalOpen) {
      // 重置主表单
      form.resetFields();
      // 重置建议表单
      adviceForm.resetFields();
      // 重置评分数据
      setEvaluationData([]);
      setInviteEvaluationDetails([]);
    }
  }, [
    appraisalModalOpen,
    form,
    adviceForm,
    setEvaluationData,
    setInviteEvaluationDetails,
  ]);

  return {
    form,
    evaluationData,
    onScoreChange,
    inviteEvaluationDetails,
    onInvitePersonChange,
    adviceProcessDone,
    queryResult,
    evalutionResultData,
    adviceForm,
    submitAdvice,
  };
}

/**
 * 维度过滤 Hook
 * 根据用户部门从考核分类中过滤出"我的维度"和"邀请评估维度"
 */
function useEvaluationDimensions(params: {
  categories: ReturnType<typeof usePerformaceEvaluationCategory>["categories"];
  supplierCategoryId: string | undefined;
  userInfo: UserInfoResult | undefined;
}) {
  const { categories, supplierCategoryId, userInfo } = params;

  const [selfDimensions, setSelfDimensions] = useState<DeimensionItem[]>();
  const [inviteDimensions, setInviteDimensions] = useState<DeimensionItem[]>();

  useEffect(() => {
    if (!userInfo) return;

    const dimensions = categories?.find(
      (c) => String(c.supplierCategoryId) === String(supplierCategoryId),
    )?.dimensions;

    const deptNo = userInfo.deptNo;

    if (deptNo && dimensions?.length) {
      setSelfDimensions(filterDimensionsByDept(dimensions, deptNo, true));
      setInviteDimensions(filterDimensionsByDept(dimensions, deptNo, false));
    }
  }, [supplierCategoryId, categories, userInfo]);

  return { selfDimensions, inviteDimensions };
}

/**
 * 我的评分数据 Hook
 */
function useSelfEvaluationData(params: {
  dimensions: DeimensionItem[] | undefined;
  userInfo: UserInfoResult | undefined;
}) {
  const { dimensions, userInfo } = params;

  const [evaluationData, setEvaluationData] = useState<EvaluationDataType[]>(
    [],
  );

  useEffect(() => {
    if (!userInfo?.userId || !userInfo.deptNo || !dimensions?.length) return;

    const data = buildSelfEvaluationData(dimensions, userInfo);
    setEvaluationData(data);
  }, [dimensions, userInfo]);

  const onScoreChange = useCallback((indicatorId: number, score: number) => {
    setEvaluationData((prev) =>
      prev.map((p) => (p.indicatorId === indicatorId ? { ...p, score } : p)),
    );
  }, []);

  return { evaluationData, onScoreChange, setEvaluationData };
}

/**
 * 邀请评估数据 Hook
 */
function useInviteEvaluationData(params: {
  dimensions: DeimensionItem[] | undefined;
}) {
  const { dimensions } = params;

  const [inviteEvaluationDetails, setInviteEvaluationDetails] = useState<
    InvitePerformanceEvaluationDetailItem[]
  >([]);

  useEffect(() => {
    if (!dimensions?.length) return;

    const data = buildInviteEvaluationData(dimensions);
    setInviteEvaluationDetails(data);
  }, [dimensions]);

  const onInvitePersonChange = useCallback<FnUpdateInvitePerson>(
    (ratingDeptId, rater) => {
      setInviteEvaluationDetails((prev) =>
        prev.map((p) =>
          p.ratingDeptId === ratingDeptId ? { ...p, rater } : p,
        ),
      );
    },
    [],
  );

  return {
    inviteEvaluationDetails,
    onInvitePersonChange,
    setInviteEvaluationDetails,
  };
}
